import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap'
import { toast } from 'react-toastify'

import instagramPlatformLogo from '../../assets/images/platforms/platform-instagram.png'
import youtubePlatformLogo from '../../assets/images/platforms/platform-youtube.png'
import { AppDispatch, RootState, store } from 'store'
import { getErrorMessage } from 'utils/helpers'
import { fetchContactsThunk } from 'slices/creators/thunk'
import { addContact } from 'services/api/creators'
import { AddContactPayload } from 'types/creators'
import { resetCreatorsState } from 'slices/creators/reducer'
import './AddCreatorContactModal.scss'
import TagsSelector from './TagsSelector'
import { toggleAddCreatorContactModal, toggleUpdateCreatorProfileModal } from 'slices/ui/reducer'

const platforms = [
    { id: 'ig', name: 'Instagram' },
    { id: 'yt', name: 'YouTube' },
]

const platformLogos: Record<string, string> = {
    ig: instagramPlatformLogo,
    yt: youtubePlatformLogo,
}

const MOBILE_COUNTRY_CODE = '+91'
const INDIAN_MOBILE_REGEX = /^[6-9]\d{9}$/

const formatMobileDigits = (value: string) => {
    const digits = value.replace(/\D/g, '')
    const withoutCountryCode = digits.startsWith('91') ? digits.slice(2) : digits
    return withoutCountryCode.replace(/^0+/, '').slice(0, 10)
}

const getMobileValidationError = (mobile: string): string | undefined => {
    const trimmed = mobile.trim()
    if (!trimmed) return undefined
    if (!INDIAN_MOBILE_REGEX.test(trimmed)) {
        return 'Please enter a valid 10-digit mobile number.'
    }
    return undefined
}

type FormErrors = { platform?: string; handle?: string; }

const AddCreatorContactModal: React.FC<{ isOpen: boolean; onClose: () => void }> = ({
    isOpen,
    onClose,
}) => {
    const [processing, setProcessing] = useState(false)
    const dispatch = useDispatch<AppDispatch>()
    const [errors, setErrors] = useState<FormErrors>({})

    const selectedHubId = useSelector((state: RootState) => state.auth.selectedHubId)

    const availablePlatforms = platforms
    const [data, setData] = useState<AddContactPayload>({
        platform:
            availablePlatforms.length === 0
                ? ''
                : availablePlatforms.length === 1
                    ? availablePlatforms[0].id
                    : 'ig',
        handle: '',
        tags: [],
    })

    const validateForm = () => {
        const newErrors: FormErrors = {}
        if (!data.platform) {
            newErrors.platform = 'Please select a social media platform.'
        }
        if (!data.handle) {
            newErrors.handle = 'Please enter your social media handle or profile link.'
        }
        // const mobileError = getMobileValidationError(data.mobile)
        // if (mobileError) {
        //     newErrors.mobile = mobileError
        // }
        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleSubmit = async () => {
        if (!validateForm() || !selectedHubId) return
        try {
            setProcessing(true)
            const response = await addContact(selectedHubId, data)
            if(response.influencer && response.influencer?.profile_completion !== 100) {
                dispatch(toggleUpdateCreatorProfileModal({ influencerId: response.influencer.uuid }))
            }
            toast.success('Creator contact added successfully.')
            dispatch(resetCreatorsState())
            dispatch(fetchContactsThunk({ force: true, filters: store.getState().creators.filters }))
            onClose()
        } catch (error: unknown) {
            console.error(error)
            toast.error(
                getErrorMessage(error) || 'Failed to add social account. Please try again.'
            )
        } finally {
            setProcessing(false)
        }
    }

    useEffect(() => {
        if (isOpen) {
            setData({
                platform: availablePlatforms[0].id,
                handle: '',
                tags: [],
            })
            setErrors({})
        }
    }, [isOpen])


    return (
        <Modal
            isOpen={isOpen}
            toggle={onClose}
            centered
            className="add-creator-contact-modal"
        >
            <ModalHeader toggle={onClose}>Add Creator Contact</ModalHeader>
            <ModalBody>
                <p className="modal-subtitle">
                    Select a platform and enter the creator&apos;s handle or profile link.
                </p>

                <div className="add-creator-contact-form">
                    <div className="mb-3">
                        <label className="form-label">
                            Platform <span className="text-danger">*</span>
                        </label>
                        <div className="platform-picker" role="radiogroup" aria-label="Platform">
                            {availablePlatforms.map((platform) => (
                                <button
                                    key={platform.id}
                                    type="button"
                                    role="radio"
                                    aria-checked={platform.id === data.platform}
                                    className={`platform-option ${platform.id === data.platform ? 'active' : ''}`}
                                    onClick={() => {
                                        setData({ ...data, platform: platform.id })
                                        setErrors((prev) => ({ ...prev, platform: undefined }))
                                    }}
                                >
                                    <img
                                        className="platform-option__icon"
                                        src={platformLogos[platform.id]}
                                        alt={platform.name}
                                    />
                                </button>
                            ))}
                        </div>
                        {errors.platform && (
                            <div className="text-danger field-error">{errors.platform}</div>
                        )}
                    </div>

                    <div className="mb-3">
                        <label htmlFor="creator-handle" className="form-label">
                            Handle / Profile Link <span className="text-danger">*</span>
                        </label>
                        <input
                            id="creator-handle"
                            value={data.handle}
                            onBlur={validateForm}
                            onChange={(e) => {
                                setData({ ...data, handle: e.target.value })
                                setErrors((prev) => ({ ...prev, handle: undefined }))
                            }}
                            type="text"
                            className="form-control"
                            name="handle"
                            placeholder="@username or profile URL"
                        />
                        {errors.handle && (
                            <div className="text-danger field-error">{errors.handle}</div>
                        )}
                    </div>

                    {/* <div className="mb-3">
                        <label htmlFor="creator-mobile" className="form-label">
                            Mobile Number
                        </label>
                        <div className="input-group mobile-input-group">
                            <span
                                className="input-group-text mobile-input-group__prefix"
                                aria-hidden
                            >
                                {MOBILE_COUNTRY_CODE}
                            </span>
                            <input
                                id="creator-mobile"
                                value={data.mobile}
                                onBlur={validateForm}
                                onChange={(e) => {
                                    setData({ ...data, mobile: formatMobileDigits(e.target.value) })
                                    setErrors((prev) => ({ ...prev, mobile: undefined }))
                                }}
                                type="tel"
                                className="form-control"
                                name="mobile"
                                placeholder="9876543210"
                                autoComplete="tel-national"
                                inputMode="numeric"
                                maxLength={10}
                                aria-describedby={errors.mobile ? 'creator-mobile-error' : undefined}
                            />
                        </div>
                        {errors.mobile && (
                            <div id="creator-mobile-error" className="text-danger field-error">
                                {errors.mobile}
                            </div>
                        )}
                    </div> */}

                    <div className="mb-0">
                        <label htmlFor="creator-tags" className="form-label">
                            Tags
                        </label>
                        <TagsSelector value={data.tags} onChange={(tags) => setData({ ...data, tags })} />
                    </div>
                </div>
            </ModalBody>
            <ModalFooter>
                <button type="button" className="theme-btn btn-primary-outlined" onClick={onClose}>
                    Cancel
                </button>
                <button
                    type="button"
                    className="theme-btn btn-primary"
                    onClick={handleSubmit}
                    disabled={processing || !data.platform || !data.handle}
                >
                    {processing ? 'Adding...' : 'Add Contact'}
                </button>
            </ModalFooter>
        </Modal>
    )
}

export default AddCreatorContactModal
